import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

// Animates the shipping-progress fill from 0 up to its real width. The bar is
// always painted server-side with its final width already set, so without
// this a CSS transition has nothing to transition from on first paint.
// On later cart updates the section gets morphed (not replaced), so the same
// node persists and the CSS transition on width fires on its own — this call
// is then a harmless no-op re-trigger, not the thing making it animate.
function animateFreeShippingBar() {
  const fill = document.querySelector('.cart-drawer__free-shipping-bar-fill');
  if (!fill) return;

  const targetWidth = fill.style.width;
  fill.style.transition = 'none';
  fill.style.width = '0%';
  fill.getBoundingClientRect();
  requestAnimationFrame(() => {
    fill.style.transition = '';
    fill.style.width = targetWidth;
  });
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function formatMoney(cents) {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fetchRecommendations(productId, intent) {
  return fetch(`${Theme.routes.product_recommendations_url}.json?product_id=${productId}&limit=6&intent=${intent}`)
    .then((response) => (response.ok ? response.json() : { products: [] }))
    .then((data) => data.products || [])
    .catch(() => []);
}

// Chevron paths copied from assets/icon-chevron-left.svg / icon-chevron-right.svg —
// this file builds markup as plain JS strings (no Liquid), so the theme's
// `inline_asset_content` filter isn't available here; inlining keeps the nav
// buttons visually consistent with the rest of the theme's icon set.
const UPSELL_CHEVRON_LEFT =
  '<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M6.5 11L1.5 6L6.5 1" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const UPSELL_CHEVRON_RIGHT =
  '<svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// One product per "page" (like OficialFarma's cart carousel) instead of a
// scrolling row of small cards — each suggestion gets the full width to
// itself, which reads as a clearer, more deliberate offer than a strip of
// cards competing for attention. Prev/next + dots only render when there's
// more than one product to page through.
function getUpsellMarkup(products) {
  const cards = products
    .map((product) => {
      const variant = product.variants[0];
      return `
        <div class="cart-drawer__upsell-card">
          <a href="${product.url}" class="cart-drawer__upsell-image" tabindex="-1" aria-hidden="true">
            <img src="${product.featured_image}" alt="" loading="lazy" width="120" height="120">
          </a>
          <div class="cart-drawer__upsell-info">
            <a href="${product.url}" class="cart-drawer__upsell-name">${escapeHtml(product.title)}</a>
            <span class="cart-drawer__upsell-price">${formatMoney(variant.price)}</span>
            <button
              type="button"
              class="button cart-drawer__upsell-add"
              data-upsell-add-button
              data-variant-id="${variant.id}"
            >
              Adicionar
            </button>
          </div>
        </div>
      `;
    })
    .join('');

  const hasMultiple = products.length > 1;

  const dots = hasMultiple
    ? `<div class="cart-drawer__upsell-dots" data-upsell-dots>${products
        .map((_, index) => `<span class="cart-drawer__upsell-dot${index === 0 ? ' cart-drawer__upsell-dot--active' : ''}"></span>`)
        .join('')}</div>`
    : '';

  return `
    <p class="cart-drawer__upsell-title">Você também pode gostar</p>
    <div class="cart-drawer__upsell-carousel">
      ${hasMultiple ? `<button type="button" class="cart-drawer__upsell-nav cart-drawer__upsell-nav--prev" data-upsell-prev aria-label="Produto anterior">${UPSELL_CHEVRON_LEFT}</button>` : ''}
      <div class="cart-drawer__upsell-track" data-upsell-track>${cards}</div>
      ${hasMultiple ? `<button type="button" class="cart-drawer__upsell-nav cart-drawer__upsell-nav--next" data-upsell-next aria-label="Próximo produto">${UPSELL_CHEVRON_RIGHT}</button>` : ''}
    </div>
    ${dots}
  `;
}

// Prev/next buttons page the track by one card width. Delegated on document,
// same pattern as onUpsellAddClick, since the carousel markup is rebuilt
// (and any directly-attached listeners lost) every time renderUpsellWith runs.
function onUpsellNavClick(event) {
  const button = event.target.closest('[data-upsell-prev], [data-upsell-next]');
  if (!button) return;

  const track = button.closest('.cart-drawer__upsell-carousel')?.querySelector('[data-upsell-track]');
  if (!track) return;

  const direction = button.hasAttribute('data-upsell-prev') ? -1 : 1;
  track.scrollBy({ left: track.clientWidth * direction, behavior: 'smooth' });
}

document.addEventListener('click', onUpsellNavClick);

// Keeps the dots in sync with whichever card is actually in view — covers
// both the nav buttons and the shopper swiping the track directly. Scroll
// events don't reliably bubble, so this listens on the capture phase instead
// of delegating normally.
document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target?.closest?.('[data-upsell-track]');
    if (!track) return;

    const dots = track.closest('.cart-drawer__upsell')?.querySelectorAll('.cart-drawer__upsell-dot');
    if (!dots?.length) return;

    const activeIndex = Math.round(track.scrollLeft / track.clientWidth);
    dots.forEach((dot, index) => dot.classList.toggle('cart-drawer__upsell-dot--active', index === activeIndex));
  },
  { capture: true, passive: true }
);

// Guards against a stale render clobbering a newer one: renderUpsell() (fired
// on page load) and renderUpsellFast() (fired on add-to-cart) can both be
// in flight at once when a shopper adds an item right after the page loads.
// If the page-load call happened to capture an empty cart (typical for a
// first-time visit) and resolves AFTER the add-to-cart call has already
// populated the container, it wipes it back out — confirmed live: the first
// add after a fresh page load silently failed to show recommendations, but a
// second add (with no earlier in-flight call left to race) worked every
// time. Each render call claims the counter as its own id before writing;
// if a newer call has claimed it since, the older one's result is discarded.
let upsellRenderId = 0;

// #CartDrawer-Upsell only exists in the server-rendered markup when the cart
// already has items (cart-drawer.liquid only prints it in the non-empty-cart
// branch) — on a fully empty cart it isn't in the DOM at all until the first
// add-to-cart's own section morph inserts it for the first time. Our render
// calls run off the same cartLinesUpdate event/promise as that morph, so on
// a first add there's a genuine race between "morph creates the container"
// and "we go looking for it" — confirmed live: the container simply didn't
// exist yet when our code checked, so it silently did nothing every time on
// a first add to an empty cart, while a second add (container already
// created by the first) worked fine. This waits for the container to
// actually exist instead of assuming it's already there.
function waitForUpsellContainer(timeoutMs = 4000) {
  const existing = document.getElementById('CartDrawer-Upsell');
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const observer = new MutationObserver(() => {
      const container = document.getElementById('CartDrawer-Upsell');
      if (!container) return;
      observer.disconnect();
      clearTimeout(timer);
      resolve(container);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timer = setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeoutMs);
  });
}

// Fetches recommendations for baseProductId and writes the result into
// #CartDrawer-Upsell, excluding anything in excludeProductIds. Shared by both
// renderUpsell() and renderUpsellFrom(), which differ only in how they learn
// the current cart contents (a fresh fetch vs. data the caller already has).
function renderUpsellWith(container, baseProductId, excludeProductIds, renderId) {
  // Fetch both intents in parallel rather than falling back to 'related' only
  // after 'complementary' resolves empty — on real (especially mobile) network
  // latency, that sequential fallback doubles the wait before anything renders.
  return Promise.all([fetchRecommendations(baseProductId, 'complementary'), fetchRecommendations(baseProductId, 'related')])
    .then(([complementary, related]) => (complementary.length ? complementary : related))
    .then((products) => {
      if (renderId !== upsellRenderId) return;
      const filtered = products.filter((product) => !excludeProductIds.has(product.id) && product.available).slice(0, 4);

      if (!filtered.length) {
        container.hidden = true;
        container.innerHTML = '';
        return;
      }

      container.hidden = false;
      container.innerHTML = getUpsellMarkup(filtered);
    })
    .catch(() => {
      if (renderId !== upsellRenderId) return;
      container.hidden = true;
      container.innerHTML = '';
    });
}

// Renders a "você também pode gostar" strip in #CartDrawer-Upsell based on
// Shopify's Product Recommendations API, seeded from the most recently added
// item when known. Complementary recommendations need order history to work,
// so this falls back to related products until the store accumulates enough.
//
// Fetches the current cart first, since the caller has no cart data of its own
// (page load, or after the drawer's own quick-add where only the added item is known).
function renderUpsell(seedProductId) {
  const renderId = ++upsellRenderId;
  waitForUpsellContainer().then((container) => {
    if (!container || renderId !== upsellRenderId) return;

    fetch(`${Theme.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        if (renderId !== upsellRenderId) return;
        if (!cart.items.length) {
          container.hidden = true;
          container.innerHTML = '';
          return;
        }

        const baseProductId = seedProductId || cart.items[0].product_id;
        const cartProductIds = new Set(cart.items.map((item) => item.product_id));
        return renderUpsellWith(container, baseProductId, cartProductIds, renderId);
      })
      .catch(() => {
        if (renderId !== upsellRenderId) return;
        container.hidden = true;
        container.innerHTML = '';
      });
  });
}

// Same as renderUpsell(), but for the cartLinesUpdate listener, which already
// gets the added product's id and the new item count on the event detail — a
// fresh /cart.js round trip to learn the same thing just adds latency (real
// money on mobile, where this was slow enough to look broken). The only
// difference from renderUpsell() is the exclusion set covers just the item
// that was added, not every line in the cart — a fresh add is by far the
// common case, and worst case a pre-existing line shows up as a "suggestion".
function renderUpsellFast(productId, itemCount) {
  if (!itemCount || !productId) {
    renderUpsell(productId);
    return;
  }

  const renderId = ++upsellRenderId;
  waitForUpsellContainer().then((container) => {
    if (!container || renderId !== upsellRenderId) return;
    // event.detail.productId is a string; recommendation results carry numeric
    // ids (confirmed against the live /recommendations/products.json response),
    // so the exclusion check needs both sides normalized or it silently never matches.
    renderUpsellWith(container, productId, new Set([Number(productId)]), renderId);
  });
}

// The header's cart-icon badge and the drawer's own heading badge both sit
// outside the data-hydration-key region that hydration-mode morphing patches,
// so a section refresh alone leaves them stale. Patched directly here instead
// of switching to a full-mode morph, which would risk the same dialog
// open-state loss that hydration mode exists to avoid.
function updateCartBubbles(itemCount) {
  document.querySelectorAll('.cart-bubble').forEach((bubble) => {
    bubble.classList.toggle('visually-hidden', itemCount === 0);
    const countEl = bubble.querySelector('.cart-bubble__text-count');
    if (countEl) {
      countEl.textContent = itemCount;
      countEl.classList.toggle('hidden', itemCount === 0);
    }
  });

  document.querySelectorAll('#cart-drawer .theme-drawer__badge').forEach((badge) => {
    badge.textContent = itemCount;
    badge.classList.toggle('visually-hidden', itemCount === 0);
  });
}

// Re-fetches the cart-drawer-section HTML and morphs it in "hydration" mode —
// only nodes under data-hydration-key get patched, so the open <theme-drawer>
// dialog itself (which the fresh server HTML always renders as closed) is
// left alone instead of clobbering the live open state. This is the same
// helper CartItemsComponent uses for its own drawer refreshes.
function refreshCartDrawerSection() {
  // Must fetch the current page's own URL, not Theme.routes.cart_url (/cart):
  // cart-drawer.liquid only renders its markup when `template.name != 'cart'`
  // (the drawer is intentionally hidden on the cart page itself), so fetching
  // /cart?section_id=... makes that guard render nothing to morph in.
  const url = `${window.location.pathname}?section_id=cart-drawer-section`;
  return fetch(url)
    .then((response) => (response.ok ? response.text() : Promise.reject()))
    .then((html) => morphSection('cart-drawer-section', html, { mode: 'hydration' }));
}

function onUpsellAddClick(event) {
  const button = event.target.closest('[data-upsell-add-button]');
  if (!button || button.hasAttribute('aria-disabled')) return;

  button.setAttribute('aria-disabled', 'true');
  const originalText = button.textContent;
  button.textContent = '...';

  const formData = new FormData();
  formData.append('id', button.dataset.variantId);
  formData.append('quantity', '1');

  fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }))
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        console.error('[cart-drawer-extras] falha ao adicionar sugestão', response.description || response.message);
        return;
      }
      return refreshCartDrawerSection().then(() => {
        renderUpsell(response.product_id);
        animateFreeShippingBar();
        // The freshly fetched section HTML has no concept of the dialog's
        // live open state, so the morph can drop it. open() is idempotent —
        // it just re-focuses if already open — so this is safe either way.
        document.getElementById('cart-drawer')?.open?.();
        return fetch(`${Theme.routes.cart_url}.js`)
          .then((r) => r.json())
          .then((cart) => updateCartBubbles(cart.item_count));
      });
    })
    .catch((error) => console.error('[cart-drawer-extras] falha ao adicionar sugestão', error))
    .finally(() => {
      button.removeAttribute('aria-disabled');
      button.textContent = originalText;
    });
}

document.addEventListener('click', onUpsellAddClick);

// Fires after any native add-to-cart / quantity-change flow (product page,
// quick add, cart page) once the theme's own morph has settled the section.
//
// Must wait on event.promise: cart-items-component listens to this same event
// and, once its own promise resolves, morphs the whole drawer section in
// "hydration" mode — which re-renders everything under the drawer's
// data-hydration-key, including #CartDrawer-Upsell (on a first add to an
// empty cart, that morph is what puts the div in the DOM at all — see
// waitForUpsellContainer). If renderUpsell() writes to it before that morph
// runs, the morph wipes the content straight back out. Waiting on the same
// promise cart-items-component uses guarantees this runs after that morph
// has already settled.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  // event.detail is empty at synchronous dispatch time — productId/itemCount
  // only exist on the value the promise resolves to (confirmed live: raw
  // event.detail has no keys at all until then). Reading them synchronously
  // like the old code did meant renderUpsellFast always saw undefined and
  // silently fell back to the slow path on every single call.
  event.promise
    ?.then((resolved) => {
      const seedProductId = resolved?.detail?.productId;
      const itemCount = resolved?.detail?.itemCount;
      requestAnimationFrame(() => {
        // renderUpsellFast uses the id/count already on this event instead of
        // renderUpsell's own /cart.js round trip — see its doc comment.
        renderUpsellFast(seedProductId, itemCount);
        animateFreeShippingBar();
      });
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-drawer-extras] Event promise rejected:', error);
    });
});

document.addEventListener('DOMContentLoaded', () => {
  renderUpsell();
  animateFreeShippingBar();
});
